import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPublications } from '../services/wordpress';
import {
    Filter,
    Download,
    Book,
    Eye,
    AlertTriangle
} from 'lucide-react';
import { Publication } from '../types';
import { safeHTML } from '../utils/safeHTML';
import { useToast } from './Toast';
import './Repository.css';

const Repository: React.FC = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [publications, setPublications] = useState<Publication[]>([]);
    const [loading, setLoading] = useState(true);

    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    useEffect(() => {
        async function loadData() {
            const data = await getPublications();
            setPublications(data);
            setLoading(false);
        }
        loadData();
    }, []);

    const years = useMemo(() => {
        const uniqueYears = Array.from(new Set(publications.map(p => p.year)));
        return uniqueYears.sort((a: number, b: number) => b - a);
    }, [publications]);

    const types = useMemo(() => Array.from(new Set(publications.map(p => p.type))), [publications]);

    const filteredPublications = useMemo(() => {
        return publications.filter(p => {
            const matchYear = selectedYear ? p.year === selectedYear : true;
            const matchType = selectedType ? p.type === selectedType : true;
            const matchCategory = selectedCategory ? p.category === selectedCategory : true;
            return matchYear && matchType && matchCategory;
        });
    }, [publications, selectedYear, selectedType, selectedCategory]);

    const clearFilters = () => {
        setSelectedYear(null);
        setSelectedType(null);
        setSelectedCategory(null);
    };

    const handleDownload = (urlInput: string | any, title: string) => {
        let targetUrl = '';

        if (typeof urlInput === 'string') {
            targetUrl = urlInput;
        } else if (typeof urlInput === 'object' && urlInput !== null) {
            targetUrl = urlInput.url || urlInput.source_url || urlInput.link || '';
        }

        if (!targetUrl || targetUrl === '#' || targetUrl === '' || (typeof targetUrl === 'string' && targetUrl.includes('[object'))) {
            showToast("Tautan file PDF tidak ditemukan atau format data dari WordPress salah.", "error");
            return;
        }

        const link = document.createElement('a');
        link.href = targetUrl;
        link.setAttribute('download', `${title.replace(/<[^>]+>/g, '').trim()}.pdf`);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <div className="Repository_page">
            <Helmet>
                <title>Riset & Publikasi - Lakpesdam PBNU</title>
                <meta name="description" content="Pusat pengetahuan dan data strategis Lakpesdam PBNU. Jurnal, buku, policy brief, dan laporan riset terbaru." />
                <meta property="og:title" content="Riset & Publikasi - Lakpesdam PBNU" />
                <meta property="og:description" content="Pusat pengetahuan dan data strategis Lakpesdam PBNU." />
                <meta property="og:image" content="https://lakpesdam.or.id/assets/og-image.png" />
                <meta property="og:type" content="website" />
                <link rel="canonical" href="https://lakpesdam.or.id/publikasi" />
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    "name": "Riset & Publikasi - Lakpesdam PBNU",
                    "description": "Pusat pengetahuan dan data strategis Lakpesdam PBNU. Jurnal, buku, policy brief, dan laporan riset terbaru.",
                    "url": "https://lakpesdam.or.id/publikasi"
                  })}
                </script>
            </Helmet>
            <div className="container">
                <div className="Repository_header">
                    <h1 className="Repository_title">Riset & Publikasi</h1>
                    <p className="Repository_subtitle">Pusat pengetahuan dan data strategis Lakpesdam PBNU.</p>
                </div>

                <div className="Repository_layout">
                    <aside className="Repository_sidebar">
                        <div className="Repository_stickyWrapper">
                            <div className="Repository_filterHeader">
                                <h3 className="Repository_filterTitle"><Filter size={18} /> Filter</h3>
                                {(selectedYear || selectedType || selectedCategory) && (
                                    <button onClick={clearFilters} className="Repository_resetBtn">Reset</button>
                                )}
                            </div>
                            {loading ? <div className="text-sm text-slate-400 py-4">Memuat filter...</div> : (
                                <>
                                    {years.length > 0 && (
                                        <div>
                                            <h4 className="Repository_filterGroupTitle">Tahun Terbit</h4>
                                            <div className="Repository_filterList" role="radiogroup" aria-label="Filter tahun terbit">
                                                {years.map(year => (
                                                    <label key={year} className="Repository_filterItem">
                                                        <div className={`Repository_checkbox ${selectedYear === year ? 'Repository_checkboxActive' : ''}`} aria-checked={selectedYear === year} role="radio">
                                                            {selectedYear === year && <div className="Repository_checkMark" />}
                                                        </div>
                                                        <input type="radio" className="hidden" onChange={() => setSelectedYear(selectedYear === year ? null : year)} aria-label={`Tahun ${year}`} />
                                                        <span className={`Repository_filterLabel ${selectedYear === year ? 'Repository_filterLabelActive' : ''}`}>{year}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {types.length > 0 && (
                                        <div>
                                            <h4 className="Repository_filterGroupTitle">Jenis Dokumen</h4>
                                            <div className="Repository_filterList" role="radiogroup" aria-label="Filter jenis dokumen">
                                                {types.map(type => (
                                                    <label key={type} className="Repository_filterItem">
                                                        <div className={`Repository_checkbox ${selectedType === type ? 'Repository_checkboxActive' : ''}`} aria-checked={selectedType === type} role="radio" />
                                                        <input type="radio" className="hidden" onChange={() => setSelectedType(selectedType === type ? null : type)} aria-label={`Jenis: ${type}`} />
                                                        <span className={`Repository_filterLabel ${selectedType === type ? 'Repository_filterLabelActive' : ''}`}>{type}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </aside>

                    <main className="Repository_main">
                        {loading ? (
                          <div className="Repository_grid" style={{ width: '100%' }}>
                            {[1, 2, 3, 4, 5, 6].map(i => (
                              <div key={i} className="Repository_card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', border: '1px solid #e2e8f0', padding: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div style={{ width: '80px', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                                  <div style={{ width: '40px', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                                </div>
                                <div style={{ width: '100%', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                                <div style={{ width: '80%', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                                <div style={{ width: '100%', height: '60px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite', marginTop: '0.5rem' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                                  <div style={{ width: '120px', height: '32px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                                  <div style={{ width: '100px', height: '20px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                                </div>
                              </div>
                            ))}
                            <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }`}</style>
                          </div>
                        ) : (
                            <>
                                <div className="Repository_resultsInfo">
                                    <span className="Repository_count">Menampilkan <span className="Repository_countNum">{filteredPublications.length}</span> publikasi</span>
                                </div>
                                <div className="Repository_grid">
                                    {filteredPublications.map((pub: Publication) => (
                                        <div key={pub.id} className="Repository_card cursor-pointer"
                                            onClick={() => navigate(`/${pub.slug || pub.id}`)}>

                                            <div className="Repository_cardHeader">
                                                <span className="Repository_typeBadge Repository_typeBadgeDefault"><Book size={16} /> {pub.type}</span>
                                                <span className="Repository_year">{pub.year}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
                                              {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                                              <h3 className="Repository_cardTitle" {...safeHTML(pub.title)} aria-label={pub.title.replace(/<[^>]+>/g, '').trim()} style={{ flex: 1 }} />
                                              {pub.isFallback && (
                                                <div style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: 'rgba(255, 255, 255, 0.9)', color: '#d97706', padding: '0.25rem 0.5rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #d97706' }}>
                                                  <AlertTriangle size={14} /> Offline
                                                </div>
                                              )}
                                            </div>
                                            <div className="Repository_excerpt" {...safeHTML(pub.excerpt)} />
                                            <div className="Repository_cardFooter">
                                                <div className="flex gap-2">
                                                    {pub.pdfUrl && pub.pdfUrl !== '#' && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/${pub.slug || pub.id}`); }}
                                                            className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-md text-xs font-bold hover:bg-emerald-100 transition"
                                                        >
                                                            <Eye size={14} /> Lihat
                                                        </button>
                                                    )}
                                                    {pub.is_downloadable && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDownload(pub.pdfUrl, pub.title); }}
                                                            className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1.5 rounded-md text-xs font-bold hover:bg-amber-100 transition"
                                                        >
                                                            <Download size={14} /> Unduh
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="Repository_author">{pub.author.name}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default Repository;
